import { useRef } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Form,
  FormGroup,
  NavLink,
  Spinner,
  Row,
  Table,
} from 'reactstrap';
import { FilePreviewTypes } from '../../../../constants/formConstants';
import SectionTitle from './SectionTitle';
import usePropertySalesInfo from '../../hooks/usePropertySalesInfo';
import { Controller } from 'react-hook-form';
import AdminUploadFile from '../../../../components/admin/AdminUploadFile';

interface Props {
  // Formulario compartido con "Configurar venta" (una sola instancia, creada en la pantalla).
  salesInfo: ReturnType<typeof usePropertySalesInfo>;
}

const toggleFields = [
  { name: 'mortgage', label: 'Hipoteca' },
  { name: 'free_of_lien', label: 'Libre de gravamen' },
  { name: 'has_extracted_letter', label: 'Tiene carta extractada' },
  { name: 'has_scriptures', label: 'Tiene escrituras' },
] as const;

const PropertySalesInfo = ({ salesInfo }: Props) => {
  const {
    // form
    control,
    handleSubmit,
    handlePropertySalesAction,
    handleUploadExtractedLetter,
    handleUploadScriptures,
    handleUploadPurchaseAndSaleDoc,
    // loading
    propertySalesInfo,
    isLoading,
  } = salesInfo;

  // Solo la primera carga deja el formulario vacío: al guardar (toast, sin recargar la página) el
  // formulario se queda en pantalla con el botón en "cargando" en vez de desaparecer y volver.
  const hasRendered = useRef(false);
  if (isLoading && !hasRendered.current) {
    return <></>;
  }
  hasRendered.current = true;

  return (
    <>
      <Row>
        <Col md="12">
          <Card>
            <CardHeader className="py-3">
              <h3 className="mb-0">Información de propiedad en venta</h3>
            </CardHeader>
            <Form
              role="form"
              onSubmit={handleSubmit((form) => handlePropertySalesAction(form))}
            >
              <CardBody className="py-3">
                <SectionTitle className="mt-2" title="Condiciones legales" />
                <Row>
                  {toggleFields.map(({ name, label }) => (
                    <Col lg="3" md="6" key={name}>
                      <FormGroup className="d-flex align-items-center justify-content-between mb-3">
                        <label
                          className="form-control-label mb-0"
                          htmlFor={name}
                        >
                          {label}
                        </label>
                        <Controller
                          name={name}
                          control={control}
                          render={({ field }) => (
                            <label className="custom-toggle mb-0">
                              <input
                                id={name}
                                type="checkbox"
                                checked={!!field.value}
                                onChange={(e) =>
                                  field.onChange(e.target.checked)
                                }
                              />
                              <span
                                className="custom-toggle-slider rounded-circle"
                                data-label-off="No"
                                data-label-on="Sí"
                              />
                            </label>
                          )}
                        />
                      </FormGroup>
                    </Col>
                  ))}
                </Row>

              </CardBody>
              <CardFooter className="text-right py-3">
                <Button color="primary" disabled={isLoading}>
                  {isLoading && <Spinner size="sm" className="mr-2" />}
                  {propertySalesInfo?.id
                    ? 'Guardar cambios'
                    : 'Crear información de venta'}
                </Button>
              </CardFooter>
            </Form>
          </Card>
        </Col>
        {propertySalesInfo?.finances &&
          propertySalesInfo.finances.length > 0 && (
            <Col md="12">
              <Card>
                <CardHeader className="py-3">
                  <h3 className="mb-0">Detalle financiero</h3>
                </CardHeader>
                <Table className="align-items-center table-flush" responsive>
                  <thead className="thead-light">
                    <tr>
                      <th scope="col">Concepto</th>
                      <th scope="col" className="text-right">
                        Valor
                      </th>
                      <th scope="col">Descripción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {propertySalesInfo.finances.map((finance) => (
                      <tr key={finance.text}>
                        <td>{finance.text}</td>
                        <td
                          className="text-right"
                          style={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {finance.value}
                        </td>
                        <td style={{ color: '#525f7f' }}>
                          {finance.description}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>
            </Col>
          )}
      </Row>
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <h6 className="heading-small text-muted">Subida de archivos</h6>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="6">
                  <div className="col ml--2">
                    {propertySalesInfo?.extracted_letter_url && (
                      <NavLink
                        href={propertySalesInfo?.extracted_letter_url}
                        target="_blank"
                      >
                        <i className={'fa fa-duotone fa-file-contract'} /> Ver
                        carta extractada existente
                      </NavLink>
                    )}
                  </div>
                  {propertySalesInfo?.has_extracted_letter && (
                    <AdminUploadFile
                      handleUpload={handleUploadExtractedLetter}
                      description={'Subir Carta Extractada'}
                      maxFiles={1}
                      dropZoneText="Formatos soportados: PDF máximo de 10MB. Solo se puede subir 1 archivo; si subes otro, reemplaza el actual."
                      filesPreviewType={FilePreviewTypes.COUNT}
                    />
                  )}
                </Col>
                <Col md="6">
                  <div className="col ml--2">
                    {propertySalesInfo?.scriptures_url && (
                      <NavLink
                        href={propertySalesInfo?.scriptures_url}
                        target="_blank"
                      >
                        <i className={'fa fa-duotone fa-file-contract'} /> Ver
                        escrituras existentes
                      </NavLink>
                    )}
                  </div>
                  {propertySalesInfo?.has_scriptures && (
                    <AdminUploadFile
                      handleUpload={handleUploadScriptures}
                      description={'Subir Escrituras'}
                      maxFiles={1}
                      dropZoneText="Formatos soportados: PDF máximo de 10MB. Solo se puede subir 1 archivo; si subes otro, reemplaza el actual."
                      filesPreviewType={FilePreviewTypes.COUNT}
                    />
                  )}
                </Col>
                <Col md="6">
                  <div className="col ml--2">
                    {propertySalesInfo?.purchase_and_sale_doc_url && (
                      <NavLink
                        href={propertySalesInfo?.purchase_and_sale_doc_url}
                        target="_blank"
                      >
                        <i className={'fa fa-duotone fa-file-contract'} /> Ver
                        acuerdo de compra venta existente
                      </NavLink>
                    )}
                  </div>
                  {propertySalesInfo?.id ? (
                    <AdminUploadFile
                      handleUpload={handleUploadPurchaseAndSaleDoc}
                      description={'Subir acuerdo compra venta'}
                      maxFiles={1}
                      dropZoneText="Formatos soportados: PDF máximo de 10MB. Solo se puede subir 1 archivo; si subes otro, reemplaza el actual."
                      filesPreviewType={FilePreviewTypes.COUNT}
                    />
                  ) : (
                    <small style={{ color: '#525f7f' }}>
                      Guarda primero la información de venta para poder subir el
                      acuerdo de compra venta.
                    </small>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PropertySalesInfo;
